import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FinancialOverviewCard } from '../components/ui/FinancialOverviewCard';
import { EnhancedCreditCard } from '../components/ui/EnhancedCreditCard';
import { TrendChart, EnhancedBarChart, EnhancedPieChart } from '../components/charts/AdvancedCharts';
import { mockCreditCards, mockAnalyticsData, mockFinancialOverview } from '../data/mockData';

const StyledContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 24px;
`;

const StyledInnerContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const StyledTitle = styled(motion.h1)`
  text-align: center;
  color: white;
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 48px;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 32px;
  }
`;

const StyledSection = styled(motion.section)`
  margin-bottom: 48px;

  @media (max-width: 768px) {
    margin-bottom: 32px;
  }
`;

const StyledSectionTitle = styled.h2`
  color: white;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 24px;
  text-align: center;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 16px;
  }
`;

const StyledChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StyledOverviewContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 768px) {
    padding: 20px;
    border-radius: 16px;
  }
`;

const StyledDemo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
    border-radius: 16px;
  }
`;

export const ShowcasePage: React.FC = () => (
  <StyledContainer>
    <StyledInnerContainer>
      <StyledTitle
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        🚀 Finance Friend - Enhanced UI Showcase
      </StyledTitle>

      <StyledSection
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <StyledSectionTitle>Financial Overview Card</StyledSectionTitle>
        <StyledOverviewContainer>
          <FinancialOverviewCard overview={mockFinancialOverview} loading={false} />
        </StyledOverviewContainer>
      </StyledSection>

      <StyledSection
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <StyledSectionTitle>Enhanced Credit Cards</StyledSectionTitle>
        <StyledDemo>
          <EnhancedCreditCard cards={mockCreditCards} />
        </StyledDemo>
      </StyledSection>

      <StyledSection
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <StyledSectionTitle>Advanced Charts & Analytics</StyledSectionTitle>
        <StyledDemo>
          <StyledChartsGrid>
            <TrendChart data={mockAnalyticsData.monthlyTrends} title='Monthly Income vs Expenses Trend' height={350} />
            <EnhancedPieChart
              data={mockAnalyticsData.categoryBreakdown}
              title='Spending Categories Breakdown'
              height={350}
            />
          </StyledChartsGrid>

          <EnhancedBarChart data={mockAnalyticsData.monthlyTrends} title='Comparative Monthly Analysis' height={300} />
        </StyledDemo>
      </StyledSection>

      <StyledSection
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <StyledSectionTitle>🎨 Features Highlights</StyledSectionTitle>
        <StyledDemo>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px',
              color: 'white',
            }}
          >
            <div style={{ padding: '20px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '12px' }}>
              <h3>✨ Revolut-Style Animations</h3>
              <p>Smooth framer-motion animations, holographic effects, and micro-interactions</p>
            </div>
            <div style={{ padding: '20px', background: 'rgba(99, 102, 241, 0.2)', borderRadius: '12px' }}>
              <h3>📱 Perfect Mobile Support</h3>
              <p>Responsive design that works flawlessly on all screen sizes</p>
            </div>
            <div style={{ padding: '20px', background: 'rgba(245, 158, 11, 0.2)', borderRadius: '12px' }}>
              <h3>📊 Advanced Tooltips</h3>
              <p>Interactive charts with beautiful, informative tooltips and data visualization</p>
            </div>
            <div style={{ padding: '20px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '12px' }}>
              <h3>🎭 Dynamic Effects</h3>
              <p>Shimmer effects, gradient animations, and depth-based shadows</p>
            </div>
          </div>
        </StyledDemo>
      </StyledSection>
    </StyledInnerContainer>
  </StyledContainer>
);

export default ShowcasePage;
